import { ApplicationRef, ComponentFactoryResolver, ComponentRef, EventEmitter, Injectable, Injector, Type } from '@angular/core';
import { ScrollbarComponent } from '@common/UI/scrollbar/scrollbar.component';
import { LayoutService } from './layout.service';

let active: ModalRef[] = [];

@Injectable({
  providedIn: 'root',  
})
export class ModalService {

  constructor(private resolver: ComponentFactoryResolver, private app: ApplicationRef, private injector: Injector, private layout: LayoutService) { }

  public show<T>(component: Type<T>, options?: ModalOptions<T>): ModalRef<T> {
    return new _ModalRef(component, options, this.app, this.resolver, this.layout, this.injector);
  }

  public of<T>(component: T): ModalRef<T> {
    return component['modal-ref'];
  }

  public hide<T>(component?: T) {

    if (!component) {
      if (active.length > 0) active.last().hide();
    }
    else {

      let ref = this.of(component);
      if (ref) ref.hide();
    }
  }

  public onHide<T>(component?: T) {

    if (!component) {
      return active.length > 0 ? active.last().onHide : undefined;
    }

    let ref = this.of(component);
    return ref ? ref.onHide : undefined;
  }

  public content<T = any>() : T {
    return active.length > 0 ? active.last().content : undefined;
  }

  public closable<T>(value: boolean, component?: T) {
    if (!component) {
      if (active.length > 0) active.last().closable = value;
    }
    else {
      let ref = this.of(component);
      if (ref) ref.closable = value;
    }
  }
}

class _ModalRef<T = any> implements ModalRef<T> {

  private _component: ComponentRef<any>;
  private _scrollbar: ComponentRef<ScrollbarComponent>;
  private _backdrop: HTMLElement = document.createElement('backdrop');
  private _hidden: boolean = false;
  private _observing = document.createElement('div');
  private _observer = new (window as any).ResizeObserver(() => this.layout.resize.emit({ width: this._observing.clientWidth, height: this._observing.clientHeight, type: 'content', scope: this._observing }));
  
  private _onHide = new EventEmitter<any>();

  public closable: boolean = true;

  public constructor(component: Type<T>, options: ModalOptions<T>, private app: ApplicationRef, private resolver: ComponentFactoryResolver, private layout: LayoutService, private injector: Injector) {

    this._component = this.resolver.resolveComponentFactory(component).create(this.injector);

    this._component.instance['modal-ref'] = this;

    if (options && options.state) Object.assign(this._component.instance, options.state);

    this._scrollbar = this.resolver.resolveComponentFactory(ScrollbarComponent).create(this.injector);

    this._backdrop.addEventListener('mousedown', e => {
      if (e.target == this._backdrop && (!options || !options.ignoreBackdropClick) && this.closable) {
        this.hide();
      }
      e.stopPropagation();
    });

    this._observing.appendChild(this._component.location.nativeElement);
    this._observer.observe(this._observing);
    this._backdrop.appendChild(this._observing);
    this._backdrop.appendChild(this._scrollbar.location.nativeElement);
    document.body.appendChild(this._backdrop);
    this.app.attachView(this._component.hostView);
    this.app.attachView(this._scrollbar.hostView);    

    active.push(this);

    if (active.length == 1) {
      document.body.classList.add('on-modal');
    }    
  }

  public get onHide(): EventEmitter<any> {
    return this._onHide;
  }

  public hide() {

    if (!this._hidden) {      

      this.onHide.emit();

      if (this._component) {
        
        this._observer.disconnect();
        this.app.detachView(this._component.hostView);
        this.app.detachView(this._scrollbar.hostView);
        this._component.destroy();
        this._scrollbar.destroy();
        document.body.removeChild(this._backdrop);

        active = active.filter(i => i != this);

        if (active.length == 0) {
          document.body.classList.remove('on-modal');
        }
      }
      else {
        this._hidden = true;
      }      
    }
  }  

  public get content(): T {
    return this._component ? this._component.instance : undefined;
  }
}


export interface ModalRef<T = any> {
  hide(): void;
  onHide: EventEmitter<any>;
  content: T;
  closable: boolean;
}

export interface ModalOptions<T> {
  ignoreBackdropClick?: boolean;
  state?: Partial<T>;
}
